import { NgFor, NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, AfterViewInit } from '@angular/core';

@Component({
    standalone: true,
    imports: [NgIf, NgFor],
  selector: 'voice-activity-indicator',
  templateUrl: './voice-activity-indicator.component.html',
  styleUrls: ['./voice-activity-indicator.component.scss']
})
export class VoiceActivityIndicatorComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() active: boolean = false;
  barHeights: number[] = Array(12).fill(8); // 12 barras verticales

  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private microphone?: MediaStreamAudioSourceNode;
  private dataArray?: any;
  private animationId?: number;
  private stream?: MediaStream;

  ngAfterViewInit(): void {
    if (this.active) {
      setTimeout(() => this.start(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']) {
      if (this.active) {
        setTimeout(() => this.start(), 0);
      } else {
        this.stop();
      }
    }
  }

  async start() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.microphone.connect(this.analyser);
      this.animate();
    } catch (e) {
      // Silencioso para UX, pero puedes mostrar un toast si lo deseas
    }
  }

  animate = () => {
    if (!this.analyser || !this.dataArray) return;
    this.analyser.getByteFrequencyData(this.dataArray);
    const bars = this.barHeights.length;
    const step = Math.floor(this.dataArray.length / bars);
    // Calcular el volumen general para aumentar la sensibilidad global
    let total = 0;
    for (let k = 0; k < this.dataArray.length; k++) {
      total += this.dataArray[k];
    }
    let globalAvg = total / this.dataArray.length;
    // Ajustar el divisor para que la normalización permita valores más altos
    let globalLevel = Math.min(1, globalAvg / 80); // Más sensible y permite llegar más alto
    for (let i = 0; i < bars; i++) {
      let sum = 0;
      for (let j = 0; j < step; j++) {
        sum += this.dataArray[i * step + j];
      }
      let avg = sum / step;
      // Aumentar sensibilidad y respuesta máxima
      let sensitivity = 1.2; // Menor exponente para respuesta más lineal
      let base = Math.pow((avg / 255), sensitivity) * 100;
      // Mezclar con el nivel global para que todas se muevan
      let mixed = base * 0.5 + (globalLevel * 100) * 0.5;
      // Variación individual para cada barra
      mixed += Math.random() * 10 - 5;
      // Limitar el valor
      this.barHeights[i] = Math.max(8, Math.min(100, mixed));
    }
    this.animationId = requestAnimationFrame(this.animate);
  };

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = undefined;
    }
    this.barHeights = Array(12).fill(8);
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
